import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ProductCard from '@/components/ui/ProductCard'; 
import CategoryCard from '@/components/CategoryCard';
import Newsletter from '@/components/Newsletter';
import { getCategories } from '@/lib/categories'; 
import { fetchProducts } from '@/lib/api'; 
import { Category } from '@/types/category'; 
import { Product } from '@/lib/types'; 
import { mapProductFromAPI } from '@/lib/types';
import { Truck, CreditCard, Shield, Star, Clock, Users, Package } from 'lucide-react';

const Index = () => {
  const { data: categories = [], isLoading: loadingCategories } = useQuery<Category[]>({ 
    queryKey: ['categoriesForIndex'],
    queryFn: getCategories,
  });

  const { data: newProducts = [], isLoading: loadingNewProducts } = useQuery<Product[]>({ 
    queryKey: ['products', { filter: 'new', limit: 4 }], 
    queryFn: async () => {
      const productsFromAPI = await fetchProducts({ new: true, status: true }); 
      return productsFromAPI.slice(0, 4).map(mapProductFromAPI); 
    },
  });

  const { data: featuredProducts = [], isLoading: loadingFeaturedProducts } = useQuery<Product[]>({ 
    queryKey: ['products', { filter: 'featured', limit: 4 }],
    queryFn: async () => {
      const productsFromAPI = await fetchProducts({ featured: true, status: true });
      return productsFromAPI.slice(0, 4).map(mapProductFromAPI); 
    },
  });

  return (
    <>
      {/* Hero Section - RENOVADO */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 py-20 md:py-28 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="text-white space-y-6">
              <div className="inline-block">
                <span className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold animate-pulse">
                  🐠 Especialistas em Aquarismo
                </span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold leading-tight">
                Transforme Seu Espaço em um
                <span className="block text-cyan-300">Paraíso Aquático</span>
              </h1>
              
              <p className="text-xl text-blue-100 leading-relaxed">
                Tudo o que você precisa para criar e manter o aquário perfeito. 
                Qualidade garantida e entrega rápida!
              </p>

              {/* Benefits */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-500 rounded-full p-1">
                    <Package className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-lg">Mais de 1000 produtos disponíveis</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="bg-green-500 rounded-full p-1">
                    <Truck className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-lg">Entrega rápida e segura</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="bg-green-500 rounded-full p-1">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-lg">Garantia de qualidade total</span>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  to="/catalog"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 rounded-full transition duration-300 transform hover:scale-105 shadow-xl text-center text-lg"
                >
                  🛒 Ver Ofertas Agora
                </Link>
                <Link
                  to="/catalog"
                  className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold py-4 px-8 rounded-full transition duration-300 border-2 border-white/30 text-center text-lg"
                >
                  📖 Ver Catálogo Completo
                </Link>
              </div>
            </div>

            {/* Right Side - Image/Visual */}
            <div className="hidden md:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-3xl blur-3xl opacity-30 animate-pulse"></div>
                <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
                  <div className="text-center space-y-6">
                    <div className="text-6xl">🐠🌿🐟</div>
                    <div className="space-y-4">
                      <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-semibold">Produtos</span>
                          <span className="text-cyan-300 font-bold text-2xl">+1000</span>
                        </div>
                      </div>
                      <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-semibold">Clientes Felizes</span>
                          <span className="text-cyan-300 font-bold text-2xl">+500</span>
                        </div>
                      </div>
                      <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-semibold">Avaliação</span>
                          <span className="text-yellow-300 font-bold text-2xl flex items-center">
                            4.8 <Star className="h-5 w-5 ml-1 fill-current" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Bar - NOVO */}
      <section className="bg-gradient-to-r from-orange-500 to-orange-600 py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 text-white">
            <div className="flex items-center space-x-2">
              <Truck className="h-5 w-5" />
              <span className="font-semibold">FRETE GRÁTIS acima de R$ 200</span>
            </div>
            <div className="hidden md:block w-px h-6 bg-white/30"></div>
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span className="font-semibold">Garantia de 30 dias</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section - NOVO */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">
            Por que escolher a ALFA PEIXES?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Entrega Rápida</h3>
              <p className="text-sm text-gray-600">Receba em até 3 dias úteis</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-shadow">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Garantia Total</h3>
              <p className="text-sm text-gray-600">30 dias para trocar</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-shadow">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="h-8 w-8 text-yellow-600 fill-current" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Avaliação 4.8</h3>
              <p className="text-sm text-gray-600">+500 clientes satisfeitos</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section - MELHORADO */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Navegue por Categorias
            </h2>
            <p className="text-gray-600 text-lg">
              Encontre exatamente o que você precisa para seu aquário
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {loadingCategories ? (
               Array.from({ length: 5 }).map((_, index) => ( 
                 <div key={index} className="animate-pulse bg-gray-200 h-40 rounded-xl"></div>
               ))
            ) : categories && categories.length > 0 ? (
              categories.slice(0, 5).map((category) => ( 
                <CategoryCard key={category.id} category={category} />
              ))
            ) : (
              <div className="col-span-full text-center py-8">
                <p className="text-gray-500">Nenhuma categoria encontrada.</p>
              </div>
            )}
          </div>
           {categories.length > 5 && (
                <div className="text-center mt-10">
                    <Link 
                      to="/catalog" 
                      className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-full transition-colors"
                    >
                        Ver todas as categorias →
                    </Link>
                </div>
            )}
        </div>
      </section>

      {/* New Products Section - MELHORADO */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                🆕 Novidades
              </h2>
              <p className="text-gray-600">Confira os últimos lançamentos</p>
            </div>
            <Link 
              to="/catalog?filter=new" 
              className="hidden md:inline-block text-blue-600 hover:text-blue-800 font-semibold text-lg"
            >
              Ver todas →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {loadingNewProducts ? (
              Array.from({ length: 4 }).map((_, index) => ( 
                <div key={index} className="animate-pulse">
                   <div className="bg-gray-200 h-64 rounded-xl mb-3"></div>
                   <div className="bg-gray-200 h-4 w-3/4 rounded mb-2"></div>
                   <div className="bg-gray-200 h-6 w-1/2 rounded"></div>
                </div>
              ))
            ) : newProducts && newProducts.length > 0 ? (
              newProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500 text-lg">Nenhuma novidade encontrada no momento.</p>
              </div>
            )}
          </div>
          <div className="text-center mt-8 md:hidden">
            <Link 
              to="/catalog?filter=new" 
              className="inline-block text-blue-600 hover:text-blue-800 font-semibold"
            >
              Ver todas as novidades →
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products Section - MELHORADO */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                ⭐ Produtos em Destaque
              </h2>
              <p className="text-gray-600">Os mais vendidos e bem avaliados</p>
            </div>
            <Link 
              to="/catalog?filter=featured" 
              className="hidden md:inline-block text-blue-600 hover:text-blue-800 font-semibold text-lg"
            >
              Ver todos →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {loadingFeaturedProducts ? (
              Array.from({ length: 4 }).map((_, index) => ( 
                 <div key={index} className="animate-pulse">
                   <div className="bg-gray-200 h-64 rounded-xl mb-3"></div>
                   <div className="bg-gray-200 h-4 w-3/4 rounded mb-2"></div>
                   <div className="bg-gray-200 h-6 w-1/2 rounded"></div>
                 </div>
               ))
            ) : featuredProducts && featuredProducts.length > 0 ? (
              featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500 text-lg">Nenhum produto em destaque encontrado.</p>
              </div>
            )}
          </div>
          <div className="text-center mt-8 md:hidden">
            <Link 
              to="/catalog?filter=featured" 
              className="inline-block text-blue-600 hover:text-blue-800 font-semibold"
            >
              Ver todos os destaques →
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <Newsletter />
    </>
  );
};

export default Index;
